import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const getOptions = (lang) => {
  const e = lang === 'en';
  return {
    categories: [
      { value: 'cloud', label: 'Cloud / Infra' },
      { value: 'payroll', label: e ? 'Payroll' : 'Pessoal' },
      { value: 'tools', label: e ? 'Tools' : 'Ferramentas' },
      { value: 'legal', label: e ? 'Legal / Accounting' : 'Jurídico / Contábil' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'sales', label: e ? 'Sales' : 'Vendas' },
      { value: 'office', label: e ? 'Office' : 'Escritório' },
      { value: 'travel', label: e ? 'Travel' : 'Viagens' },
      { value: 'other', label: e ? 'Other' : 'Outros' },
    ],
    products: [
      { value: '', label: e ? 'General' : 'Geral' },
      { value: 'Core', label: 'Core Platform' },
      { value: 'SwimVision', label: 'SwimVision' },
      { value: 'TrackVision', label: 'TrackVision' },
      { value: 'Agents', label: 'AI Agents' },
      { value: 'Enterprise', label: 'Enterprise' },
      { value: 'Governance', label: e ? 'Governance' : 'Governança' },
    ],
    scenarios: [
      { value: 'actual', label: e ? 'Actual' : 'Real' },
      { value: 'planned', label: e ? 'Planned' : 'Planejado' },
      { value: 'demo', label: e ? 'Demo (Investor)' : 'Demo (Investidor)' },
      { value: 'conservative', label: e ? 'Conservative' : 'Conservador' },
      { value: 'base', label: 'Base' },
      { value: 'aggressive', label: e ? 'Aggressive' : 'Agressivo' },
    ],
    sources: [
      { value: 'manual', label: 'Manual' },
      { value: 'azure_finops', label: 'Azure FinOps' },
      { value: 'payroll', label: e ? 'Payroll' : 'Folha de Pagamento' },
      { value: 'legal', label: e ? 'Legal' : 'Jurídico' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'sales', label: e ? 'Sales' : 'Vendas' },
      { value: 'other', label: e ? 'Other' : 'Outro' },
    ],
    statuses: [
      { value: 'actual', label: e ? 'Completed' : 'Realizada' },
      { value: 'planned', label: e ? 'Planned' : 'Planejada' },
      { value: 'cancelled', label: e ? 'Cancelled' : 'Cancelada' },
    ],
    expenseTypes: [
      { value: 'fixed', label: e ? 'Fixed' : 'Fixa' },
      { value: 'variable', label: e ? 'Variable' : 'Variável' },
      { value: 'one_time', label: e ? 'One-time' : 'Pontual' },
    ],
    recurrences: [
      { value: 'monthly', label: e ? 'Monthly' : 'Mensal' },
      { value: 'annual', label: e ? 'Annual' : 'Anual' },
      { value: 'once', label: e ? 'Once' : 'Única' },
    ],
  };
};

const emptyForm = {
  name: '', description: '', category: 'cloud', subcategory: '',
  amount: '', currency: 'BRL', expense_date: '', reference_month: '',
  due_date: '', expense_type: 'variable', recurrence: 'monthly',
  product: '', cost_center: '', scenario_type: 'actual',
  source: 'manual', status: 'actual', is_demo: false, notes: ''
};

const Field = ({ label, children, span = 1 }) => (
  <div className={`${span > 1 ? `md:col-span-${span}` : ''}`}>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-200 focus:border-green-500 outline-none";

/**
 * FinancialExpenseModal — Create/Edit expense
 *
 * Props:
 *  - expense: objeto existente (modo edição). Quando ausente → modo criação.
 *  - defaultReferenceMonth: 'YYYY-MM' opcional. No modo CRIAÇÃO, pré-popula
 *    o campo reference_month (e expense_date) com o mês selecionado na lista.
 *    Garante que "Nova despesa" estando em Mai/2026 não cai em Jun/2026.
 *  - onClose / onSave: callbacks.
 */
const FinancialExpenseModal = ({ expense, defaultReferenceMonth, onClose, onSave }) => {
  const { lang } = useGovernanceLang();
  const T = {
    pt: {
      editTitle: 'Editar Despesa', newTitle: 'Nova Despesa',
      name: 'Nome', amount: 'Valor', category: 'Categoria',
      product: 'Produto', subcategory: 'Subcategoria', subcategoryPlaceholder: 'Opcional',
      date: 'Data', refMonth: 'Mês Referência', dueDate: 'Vencimento',
      type: 'Tipo', recurrence: 'Recorrência', costCenter: 'Centro de Custo', costCenterPlaceholder: 'Opcional',
      scenario: 'Cenário', source: 'Origem', status: 'Status',
      description: 'Descrição', descriptionPlaceholder: 'Detalhes',
      notes: 'Notas', notesPlaceholder: 'Observações internas',
      demoFlag: 'Marcar como Demo (dados demonstrativos para investidor)',
      cancel: 'Cancelar', saving: 'Salvando...', save: 'Salvar Alterações', create: 'Criar Despesa',
      errSave: 'Erro ao salvar',
    },
    en: {
      editTitle: 'Edit Expense', newTitle: 'New Expense',
      name: 'Name', amount: 'Amount', category: 'Category',
      product: 'Product', subcategory: 'Subcategory', subcategoryPlaceholder: 'Optional',
      date: 'Date', refMonth: 'Reference Month', dueDate: 'Due Date',
      type: 'Type', recurrence: 'Recurrence', costCenter: 'Cost Center', costCenterPlaceholder: 'Optional',
      scenario: 'Scenario', source: 'Source', status: 'Status',
      description: 'Description', descriptionPlaceholder: 'Details',
      notes: 'Notes', notesPlaceholder: 'Internal notes',
      demoFlag: 'Mark as Demo (demonstrative data for investor)',
      cancel: 'Cancel', saving: 'Saving...', save: 'Save Changes', create: 'Create Expense',
      errSave: 'Error saving',
    },
  }[lang] || {};
  const opts = getOptions(lang);

  const isEdit = !!expense;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (expense) {
      setForm({
        name: expense.name || '',
        description: expense.description || '',
        category: expense.category || 'other',
        subcategory: expense.subcategory || '',
        amount: expense.amount || '',
        currency: expense.currency || 'BRL',
        expense_date: expense.expense_date ? expense.expense_date.slice(0, 10) : '',
        reference_month: expense.reference_month || '',
        due_date: expense.due_date ? expense.due_date.slice(0, 10) : '',
        expense_type: expense.expense_type || 'variable',
        recurrence: expense.recurrence || 'monthly',
        product: expense.product || '',
        cost_center: expense.cost_center || '',
        scenario_type: expense.scenario_type || 'actual',
        source: expense.source || 'manual',
        status: expense.status || 'actual',
        is_demo: expense.is_demo || false,
        notes: expense.notes || '',
      });
    } else if (defaultReferenceMonth) {
      // Modo criação + mês selecionado na lista → pré-popula competência
      // (e usa dia 1 como expense_date default para alinhar com competência).
      setForm({
        ...emptyForm,
        reference_month: defaultReferenceMonth,
        expense_date: `${defaultReferenceMonth}-01`,
      });
    } else {
      setForm(emptyForm);
    }
  }, [expense, defaultReferenceMonth]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Auto-fill reference_month from expense_date
    if (field === 'expense_date' && value) {
      setForm(prev => ({ ...prev, [field]: value, reference_month: value.slice(0, 7) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        is_demo: form.is_demo ? 1 : 0,
        is_recurring: form.recurrence === 'monthly' || form.recurrence === 'annual' ? 1 : 0,
      };
      await onSave(payload, expense?.id);
      onClose();
    } catch (err) {
      setError(err.message || T.errSave);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: BRAND_GREEN }}>
          <h2 className="text-lg font-bold text-white">
            {isEdit ? T.editTitle : T.newTitle}
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={20} /></button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 text-red-700 bg-red-50 px-4 py-2 rounded-lg text-sm">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          {/* Row 1: Name + Amount */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={T.name} span={2}>
              <input value={form.name} onChange={e => handleChange('name', e.target.value)} className={inputCls} placeholder="Ex: Azure App Service P1v3" required />
            </Field>
            <Field label={T.amount}>
              <div className="flex gap-2">
                <input type="number" step="0.01" value={form.amount} onChange={e => handleChange('amount', e.target.value)} className={inputCls} required />
                <select value={form.currency} onChange={e => handleChange('currency', e.target.value)} className="border border-gray-200 rounded-lg px-2 text-sm w-20">
                  <option value="BRL">BRL</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </Field>
          </div>

          {/* Row 2: Category + Product + Subcategory */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={T.category}>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)} className={inputCls}>
                {opts.categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
            <Field label={T.product}>
              <select value={form.product} onChange={e => handleChange('product', e.target.value)} className={inputCls}>
                {opts.products.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            <Field label={T.subcategory}>
              <input value={form.subcategory} onChange={e => handleChange('subcategory', e.target.value)} className={inputCls} placeholder={T.subcategoryPlaceholder} />
            </Field>
          </div>

          {/* Row 3: Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={T.date}>
              <input type="date" value={form.expense_date} onChange={e => handleChange('expense_date', e.target.value)} className={inputCls} />
            </Field>
            <Field label={T.refMonth}>
              <input type="month" value={form.reference_month} onChange={e => handleChange('reference_month', e.target.value)} className={inputCls} />
            </Field>
            <Field label={T.dueDate}>
              <input type="date" value={form.due_date} onChange={e => handleChange('due_date', e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* Row 4: Type + Recurrence + Cost Center */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={T.type}>
              <select value={form.expense_type} onChange={e => handleChange('expense_type', e.target.value)} className={inputCls}>
                {opts.expenseTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label={T.recurrence}>
              <select value={form.recurrence} onChange={e => handleChange('recurrence', e.target.value)} className={inputCls}>
                {opts.recurrences.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <Field label={T.costCenter}>
              <input value={form.cost_center} onChange={e => handleChange('cost_center', e.target.value)} className={inputCls} placeholder={T.costCenterPlaceholder} />
            </Field>
          </div>

          {/* Row 5: Scenario + Source + Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={T.scenario}>
              <select value={form.scenario_type} onChange={e => handleChange('scenario_type', e.target.value)} className={inputCls}>
                {opts.scenarios.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label={T.source}>
              <select value={form.source} onChange={e => handleChange('source', e.target.value)} className={inputCls}>
                {opts.sources.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label={T.status}>
              <select value={form.status} onChange={e => handleChange('status', e.target.value)} className={inputCls}>
                {opts.statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          {/* Row 6: Description + Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={T.description}>
              <input value={form.description} onChange={e => handleChange('description', e.target.value)} className={inputCls} placeholder={T.descriptionPlaceholder} />
            </Field>
            <Field label={T.notes}>
              <input value={form.notes} onChange={e => handleChange('notes', e.target.value)} className={inputCls} placeholder={T.notesPlaceholder} />
            </Field>
          </div>

          {/* Demo flag */}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_demo" checked={form.is_demo} onChange={e => handleChange('is_demo', e.target.checked)} className="rounded border-gray-300" />
            <label htmlFor="is_demo" className="text-sm text-gray-600">{T.demoFlag}</label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50">
              {T.cancel}
            </button>
            <button type="submit" disabled={saving} className="px-6 py-2 rounded-lg text-sm font-bold text-white flex items-center gap-2" style={{ backgroundColor: BRAND_GREEN }}>
              <Save size={16} /> {saving ? T.saving : isEdit ? T.save : T.create}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancialExpenseModal;
